import PropTypes from "prop-types";
import React, { Component } from "react";
import { reduxForm, formValueSelector } from "redux-form";
import { connect } from "react-redux";
import moment from "moment";
import { submitClaim, submitClaimSuccess, changeReferralFileName } from "actions/claimActions";
import { getValidExchangeRateValue } from "reducers/claimsReducer";
import { Button } from "cxa-components";
import FormHeading from "components/common/FormHeading";
import { filterNull } from "cxa-utils";
import {
  StringField,
  LongStringField,
  DateField,
  DecimalField,
  ComboBoxField,
  AutocompleteField,
  CheckboxField,
  IntegerField,
} from "cxa-form-field";
import CurrencyConverterField from "./CurrencyConverterField";
import { intlShape, injectIntl } from "react-intl";
import messages from "components/messagesDescription";
import _ from "lodash";
import { intlFormat } from "i18n/util";
import ReceiptField from "./ReceiptField";
import ReferralField from "./ReferralField";
import { ErrorMessage } from "../common/ErrorMessage";
import { othersFields, othersOverseasFields } from "../constants";
import { ConfirmationDialog } from "./widgets/ConfirmationDialog";
import { decodeHtmlContent } from "../../utils";

export const formName = "addEditClaim";

export const isDecimal = value =>
  typeof value === "undefined" ||
  value === "" ||
  value === null ||
  (!isNaN(value) && Number.parseFloat(value) > 0);

export const isDecimalZeroAllowed = value =>
  typeof value === "undefined" ||
  value === "" ||
  value === null ||
  (!isNaN(value) && Number.parseFloat(value) >= 0);

export const isPostalCode = input => {
  const digitsOnly = input.replace(/\D/g, "");
  return /^\d{6}$/.test(digitsOnly) ? digitsOnly : null;
};

export const getFieldMapping = (field, claim) => {
  const { name, items, dataType } = field;

  if (name.toLowerCase() === "provider" && items && items.length && claim) {
    return items.find(item => claim.hasOwnProperty(name) && item.value === claim[name])
      ? dataType
      : "Autocomplete";
  }

  return dataType;
};

const holdConvertCurrency = num => {
  let numStr = num.toString();
  return (
    numStr.includes(".") && numStr[numStr.length - 2] == "." && numStr[numStr.length - 1] == "0"
  );
};

export const fieldMappings = {
  List: ComboBoxField,
  String: StringField,
  Date: DateField,
  Decimal: DecimalField,
  Autocomplete: AutocompleteField,
  Receipt: ReceiptField,
  Referral: ReferralField,
  TextArea: LongStringField,
  Boolean: CheckboxField,
  Integer: IntegerField,
  Currency: CurrencyConverterField,
};

class ClaimSubmissionForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isBlurred: false,
    };
  }

  validateInteger = value =>
    isDecimal(value) ? undefined : this.props.intl.formatMessage(messages.invalidNumberDescription);

  validateDecimal = value =>
    isDecimal(value) ? undefined : this.props.intl.formatMessage(messages.invalidAmountDescription);

  validateDecimalZeroAllowed = value => {
    isDecimalZeroAllowed(value)
      ? undefined
      : this.props.intl.formatMessage(messages.invalidAmountDescription);
  };

  validateRequired = (value, values, { intl: { formatMessage } }) =>
    value || value === 0 ? undefined : formatMessage(messages.requiredDescription);

  validatePostalCode = (value, values, { intl: { formatMessage } }) =>
    isPostalCode(value) ? undefined : formatMessage(messages.invalidPostalCodeDescription);

  validateCheckRequired = value =>
    !value && this.props.intl.formatMessage(messages.requiredDescription);

  convertCurrencyRate = (amount, targetName, currencyCode) => {
    const {
      fields: { fields = [] } = {},
      receiptCurrency,
      changeCurrencyRate,
      change,
      receiptDate,
    } = this.props;
    const currency = fields.find(field => field.childName === targetName);
    const { config = [] } = currency || {};
    const data = {
      config,
      currencyCode,
      receiptCurrency,
      receiptDate,
    };

    const currencyConfig = getValidExchangeRateValue(data);

    // Check if there's currencyConfig available before converting
    //      to avoid breaking the receipt amount without foreign currency conversion
    if (!_.isEmpty(currencyConfig)) {
      const { currencyFrom = null, currencyTo = null, exchangeRate } = currencyConfig || {};

      // convert currency rate
      // removed fixed decimal place in foreignReceiptAmount so that conversion can run smoothly while amount is being entered one number at a time.
      if (currencyTo !== currencyFrom) {
        change("receiptAmount", (amount * exchangeRate).toFixed(2));
        change("foreignReceiptAmount", amount);
        changeCurrencyRate((amount * exchangeRate).toFixed(2), currencyTo);
      } else if (currencyTo && currencyFrom) {
        change("receiptAmount", amount.toFixed(2));
        change("foreignReceiptAmount", amount);
        changeCurrencyRate(amount.toFixed(2), currencyTo);
      } else {
        // invalidate receipt amount
        change("receiptAmount", null);
      }
    } else if (targetName === "foreignReceiptAmount") {
      change("foreignReceiptAmount", amount);
    } else {
      change("receiptAmount", amount);
    }
  };

  handleCurrencyConverter = e => {
    const amount = parseFloat(e.target.value);
    let currencyCode = this.props.receiptCurrency;
    if (!currencyCode && this.props.fields && this.props.fields.fields) {
      for (let f of this.props.fields.fields) {
        if (f.name == "receiptCurrency") {
          currencyCode = f.items && f.items[0].value;
          break;
        }
      }
    }
    if (currencyCode) {
      this.props.changeFieldValue("receiptCurrency", currencyCode);
    }
    this.convertCurrencyRate(amount, e.target.name);
  };

  onChangeCurrencyCode = e => {
    const { foreignReceiptAmount, changeCurrencyCode, changeFieldValue } = this.props;

    changeCurrencyCode({
      [e.target.name]: e.target.value,
    });

    changeFieldValue(e.target.name, e.target.value);

    this.convertCurrencyRate(
      parseFloat(foreignReceiptAmount),
      "foreignReceiptAmount",
      e.target.value
    );
  };

  componentDidUpdate(prevProps) {
    const { receiptDate, foreignReceiptAmount, receiptCurrency } = this.props;
    //  Added : conversion will trigger when foreignReceiptAmount or receiptCurrency are updated

    if (
      !isNaN(foreignReceiptAmount) &&
      foreignReceiptAmount > 0 &&
      !holdConvertCurrency(foreignReceiptAmount) &&
      (receiptDate !== prevProps.receiptDate ||
        receiptCurrency !== prevProps.receiptCurrency ||
        foreignReceiptAmount !== prevProps.foreignReceiptAmount)
    ) {
      this.convertCurrencyRate(
        parseFloat(foreignReceiptAmount),
        "foreignReceiptAmount",
        receiptCurrency
      );
    }
  }

  confirmDuplicateSubmission = () => {
    const { change, onSubmit, handleSubmit } = this.props;
    change("userAccepted", true);
    setTimeout(
      handleSubmit((values, dispatch) =>
        onSubmit({ ...values, userAccepted: true, limitAccepted: false }, dispatch, this.props)
      )
    );
  };
  cancelDuplicateSubmission = () => {
    const { clearSubmitErrors } = this.props;
    clearSubmitErrors(formName);
  };

  confirmLimitExceedSubmission = () => {
    const { change, onSubmit, handleSubmit } = this.props;
    change("limitAccepted", true);
    setTimeout(
      handleSubmit((values, dispatch) =>
        onSubmit({ ...values, limitAccepted: true }, dispatch, this.props)
      )
    );
  };
  cancelLimitExceedSubmission = () => {
    const { clearSubmitErrors } = this.props;
    clearSubmitErrors(formName);
  };

  handleProviderFieldBlur = () => {
    this.setState({ isBlurred: true });
  };

  handleProviderFieldChange = () => {
    this.setState({ isBlurred: false });
  };

  convertFieldToInput = (
    field,
    intl,
    toCurrencyCode,
    receiptCurrency,
    foreignReceiptAmount,
    claim,
    changeReferralFileName
  ) => {
    const { required, dataType, disabled, children } = field;
    field.label = intlFormat(intl, {
      id: field.label,
      defaultMessage: field.label,
    });
    if (field.dataType === "List" && field.items && field.name !== "claimantName") {
      _.forEach(
        field.items,
        item =>
          (item.label = intlFormat(intl, {
            id: item.label,
            defaultMessage: item.label,
          }))
      );
    }

    if (field.name === "receiptAmount" && toCurrencyCode) {
      field.label = `${field.label} (${toCurrencyCode})`;
    }

    if (field.name === "receiptCurrency") {
      field.selectDefaultValue = receiptCurrency;
      field.foreignReceiptAmount = foreignReceiptAmount;
    }

    if (children && children.length > 0) {
      field.children = children.map(child => {
        child.label = intlFormat(intl, {
          id: child.label,
          defaultMessage: child.label,
        });
        
        if (child.dataType === "List" && child.items) {
          _.forEach(child.items, item => {
            item.label = intlFormat(intl, {
              id: item.label,
              defaultMessage: item.label,
            });
          });
        }
        return child;
      });
    }

    const validation = [
      required && this.validateRequired,
      dataType === "Integer" && this.validateInteger,
      dataType === "Decimal" &&
        (field.name === "consultationFee" || /^dec\d$/.test(field.name)) &&
        this.validateDecimalZeroAllowed,
      dataType === "Decimal" &&
        field.name !== "consultationFee" &&
        !/^dec\d$/.test(field.name) &&
        this.validateDecimal,
      dataType === "String" && field.name === "postalCode" && this.validatePostalCode,
    ].filter(filterNull);

    return React.createElement(fieldMappings[getFieldMapping(field, claim)], {
      id: field.name,
      key: field.name,
      description: intl.formatMessage({
        id: field.toolTip,
        defaultMessage: field.toolTip,
      }),
      validate: validation,
      ...field,
      dateFormat: moment.localeData().longDateFormat("ll"),
      readOnly: disabled,
      disabled,
      changeReferralFileName,
      handleCurrencyConverter: this.handleCurrencyConverter,
      onChangeCurrencyCode: this.onChangeCurrencyCode,
      onBlur:
        dataType === "Autocomplete" && field.name === "provider" && this.handleProviderFieldBlur,
      onChange:
        dataType === "Autocomplete" && field.name === "provider" && this.handleProviderFieldChange,
    });
  };

  resetForm = e => {
    this.props.reset();
    const closeIcon = document.getElementsByClassName("react-datepicker__close-icon");
    if (closeIcon && closeIcon.length > 0) {
      for (let i = 0; i < closeIcon.length; i++) {
        closeIcon[i].click();
      }
    }
  };

  render() {
    const {
      claim,
      fields,
      handleSubmit,
      reset,
      error,
      submitting,
      benefitPlan,
      claimItem,
      mode,
      onCancel,
      limitMessages,
      intl,
      changeReferralFileName,
      consentMessage,
      consentMessageTitle,
      toCurrencyCode,
      isCPFEnabled,
      receiptCurrency,
      foreignReceiptAmount,
      hospitalField,
      getDonateCategory,
      invalid,
    } = this.props;

    const pageTitle =
      mode === "add"
        ? intl.formatMessage(
            getDonateCategory ? messages.newDonationDescription : messages.newClaimDescription
          )
        : `${
            getDonateCategory
              ? intl.formatMessage(messages.donationIDDescription)
              : fields.documentId.label
          } ${claim.id}`;
    const claimItemLabel =
      fields &&
      fields.claimItemType &&
      intl.formatMessage({
        id: fields.claimItemType.label,
        defaultMessage: fields.claimItemType.label,
      });
    const buttonSubmitTitle =
      mode === "add"
        ? intl.formatMessage(messages.submitTextDescription)
        : intl.formatMessage(messages.saveTextDescription);
    const planName = benefitPlan && benefitPlan.planName;
    const claimName =
      claimItem &&
      intl.formatMessage({
        id: claimItem.claimName,
        defaultMessage: claimItem.claimName,
      });
    const taxStatus = claimItem.isTaxable
      ? intl.formatMessage(messages.taxableDescription)
      : intl.formatMessage(messages.nonTaxableDescription);
    const taxStatusLabel =
      fields &&
      fields.taxStatus &&
      intl.formatMessage({
        id: fields.taxStatus.label,
        defaultMessage: fields.taxStatus.label,
      });

    const infoFields = [
      {
        label: intl.formatMessage(messages.benefitPlanDescription),
        value: planName,
        id: "planName",
      },
      {
        label: claimItemLabel,
        value: claimName,
        id: "claimName",
      },
      {
        label: taxStatusLabel,
        value: taxStatus,
        id: "taxStatus",
      },
    ];
    const promptIsOpen = error && error.reason === "ASK_USER_CONFIRMATION";
    const promptMessage = promptIsOpen && error.message;
    let errorMsg = !promptIsOpen && error && <ErrorMessage error={error} intl={intl} />;

    if (isCPFEnabled) {
      const cpfContributable = claimItem.cpfContributable
        ? intl.formatMessage(messages.yesTextDescription)
        : intl.formatMessage(messages.noTextDescription);
      const cpfStatusLabel =
        fields && fields.cPFContributable
          ? intl.formatMessage({
              id: fields.cPFContributable.label,
              defaultMessage: fields.cPFContributable.label,
            })
          : null;

      const cpfContributableObject = {
        label: cpfStatusLabel,
        value: cpfContributable,
        id: "cpfContributable",
      };

      infoFields.push(cpfContributableObject);
    }
    let fieldsArray = (fields && fields.fields) || [];

    let tempArr = [];
    for (const field of fieldsArray) {
      if (
        field.name === "bankCode" ||
        field.name === "branchCode" ||
        field.name === "bankAccountNo"
      ) {
        infoFields.push({ label: field.label, value: field.value, id: field.name });
        continue;
      }
      tempArr.push(field);
    }
    fieldsArray = tempArr;

    let isValidProvider = true;
    const hasProviderValue = hospitalField && hospitalField.length >= 1;
    for (const field of fieldsArray) {
      if (field.name === "provider") {
        isValidProvider = false;
        if (field.dataType === "String") {
          isValidProvider = hasProviderValue;
        } else if (field.dataType === "Autocomplete") {
          if (!isValidProvider && hospitalField) {
            const hospitalFieldDecode = decodeHtmlContent(hospitalField);
            isValidProvider = field.items.some(clinic => clinic.value === hospitalFieldDecode);
          }
        }

        if (field.readOnly || field.disabled) {
          isValidProvider = true;
        }
      }
    }

    if (this.state.isBlurred && !isValidProvider && hasProviderValue) {
      this.props.change("provider", "");
    }

    const getProviderFields = hospitalField => {
      if (!hospitalField) return [];

      const lowerHospitalField = hospitalField.toLowerCase();

      let fieldsToFormat = [];
      if (lowerHospitalField === "others") {
        fieldsToFormat = othersFields;
      } else if (lowerHospitalField === "others-overseas") {
        fieldsToFormat = othersOverseasFields;
      }

      return fieldsToFormat.map(field => ({
        ...field,
        label: intl.formatMessage({ id: field.label, defaultMessage: field.label }),
      }));
    };

    const providerFields = getProviderFields(hospitalField);

    fieldsArray = fieldsArray
      .filter(field => field.name !== "receipt")
      .map(field => {
        if (field.name === "provider" && field.dataType === "Autocomplete") {
          return [field, ...providerFields];
        } else {
          return field;
        }
      })
      .flat();

    return (
      <form onSubmit={handleSubmit}>
        <ConfirmationDialog
          instructions={promptMessage}
          isOpen={promptIsOpen}
          handleOk={
            error && error.code === 430
              ? this.confirmLimitExceedSubmission
              : this.confirmDuplicateSubmission
          }
          handleCancel={
            error && error.code === 430
              ? this.cancelLimitExceedSubmission
              : this.cancelDuplicateSubmission
          }
        />
        <article className="card">
          <section>
            <FormHeading title={pageTitle} fields={infoFields} />
            {limitMessages && <FormHeading fields={limitMessages} />}
            <fieldset>
              {fieldsArray.map(field => {
                return this.convertFieldToInput(
                  field,
                  intl,
                  toCurrencyCode,
                  receiptCurrency,
                  foreignReceiptAmount,
                  claim,
                  changeReferralFileName
                );
              })}

              {consentMessageTitle && consentMessageTitle !== "" && (
                <h3
                  dangerouslySetInnerHTML={{
                    __html: intl.messages[consentMessageTitle]
                      ? intl.messages[consentMessageTitle]
                      : consentMessageTitle,
                  }}
                />
              )}
              {consentMessage && consentMessage !== "" && (
                <p>
                  <CheckboxField validate={this.validateCheckRequired} name="consent" id="consent">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: intl.messages[consentMessage]
                          ? intl.messages[consentMessage]
                          : consentMessage,
                      }}
                    />
                  </CheckboxField>
                </p>
              )}
            </fieldset>
            {errorMsg}
          </section>
        </article>
        <p className="form__action">
          <Button primary disabled={invalid || !isValidProvider} submitting={submitting}>
            {buttonSubmitTitle}
          </Button>
          <Button type="reset" disabled={submitting} secondary onClick={this.resetForm.bind(this)}>
            {intl.formatMessage(messages.resetTextDescription)}
          </Button>
          <Button type="button" disabled={submitting} secondary onClick={onCancel}>
            {intl.formatMessage(messages.cancelTextDescription)}
          </Button>
        </p>
      </form>
    );
  }
}

ClaimSubmissionForm.propTypes = {
  claim: PropTypes.object,
  fields: PropTypes.object,
  handleSubmit: PropTypes.func,
  resetDetails: PropTypes.func,
  onCancel: PropTypes.func,
  benefitPlan: PropTypes.object,
  claimItem: PropTypes.object,
  consentMessageTitle: PropTypes.string,
  consentMessage: PropTypes.string,
  mode: PropTypes.string,
  reset: PropTypes.func,
  error: PropTypes.string,
  prisitine: PropTypes.bool,
  submitting: PropTypes.bool,
  httpError: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  limitMessages: PropTypes.array,
  submitClaimError: PropTypes.string,
  intl: intlShape.isRequired,
  changeReferralFileName: PropTypes.func,
  changeCurrencyCode: PropTypes.func,
  changeReceiptDate: PropTypes.func,
  foreignReceiptAmount: PropTypes.string,
  hospitalField: PropTypes.string,
  receiptCurrency: PropTypes.string,
  changeCurrencyRate: PropTypes.func,
  receiptDate: PropTypes.any,
  change: PropTypes.func,
  toCurrencyCode: PropTypes.string,
  isCPFEnabled: PropTypes.bool,
  changeFieldValue: PropTypes.func,
  onSubmit: PropTypes.func,
  clearSubmitErrors: PropTypes.func,
  getDonateCategory: PropTypes.bool,
  invalid: PropTypes.bool,
};

const selector = formValueSelector(formName);

export default connect(state => {
  const hospitalField = selector(state, "provider");
  return {
    hospitalField,
  };
})(
  injectIntl(
    reduxForm({
      form: formName,
      onSubmit: (values, dispatch, props) => {
        return submitClaim(values, dispatch, props);
      },
      onSubmitSuccess: submitClaimSuccess,
      enableReinitialize: true,
      keepDirtyOnReinitialize: true,
      forceUnregisterOnUnmount: true,
      changeReferralFileName,
    })(ClaimSubmissionForm)
  )
);



// WEBPACK FOOTER //
// ./components/claim/ClaimSubmissionForm.js